/**
 * ScraperRunDetail - Detail view for a scraper run
 *
 * Features:
 * - Metadata summary at top
 * - Two tabs: Changes and Full Log
 * - Changes: table of structured changes (entity, field, old → new)
 * - Full Log: monospace scrollable with color-coded levels
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminApi } from '../../../services/admin-api';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  step: string;
  message: string;
  metadata?: Record<string, any>;
}

interface ChangeEntry {
  timestamp: string;
  entity: string;
  entityId?: string;
  field: string;
  oldValue: any;
  newValue: any;
  metadata?: Record<string, any>;
}

interface DetailedLog {
  runId: string;
  tournamentId: string;
  tournamentName: string;
  scraperType: string;
  startTime: string;
  endTime: string;
  changes: ChangeEntry[];
  logs: LogEntry[];
  summary: {
    totalChanges: number;
    changesByType: Record<string, number>;
    warnings: number;
    errors: number;
  };
}

export function ScraperRunDetail() {
  const { id } = useParams<{ id: string }>();

  const [run, setRun] = useState<any>(null);
  const [detailedLog, setDetailedLog] = useState<DetailedLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingLog, setLoadingLog] = useState(false);
  const [activeTab, setActiveTab] = useState<'changes' | 'log'>('changes');

  useEffect(() => {
    if (id) {
      loadRun();
    }
  }, [id]);

  const loadRun = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const runData = await adminApi.getScraperRun(id);
      setRun(runData);

      // If S3 log exists, fetch it
      if (runData.s3_key) {
        await loadDetailedLog();
      }
    } catch (error) {
      console.error('Failed to load scraper run:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedLog = async () => {
    if (!id) return;

    try {
      setLoadingLog(true);
      // Get presigned S3 URL
      const { url } = await adminApi.getScraperRunLogUrl(id);

      // Fetch log from S3
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch log from S3');
      }

      const logData: DetailedLog = await response.json();
      setDetailedLog(logData);
    } catch (error) {
      console.error('Failed to load detailed log:', error);
    } finally {
      setLoadingLog(false);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'running':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'error':
        return 'text-red-600';
      case 'warn':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') return JSON.stringify(value);
    return value.toString();
  };

  if (!id) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">Invalid run ID</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">Scraper run not found</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center text-sm text-gray-500">
        <Link to="/admin" className="hover:text-gray-700">
          Admin
        </Link>
        <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <Link to="/admin/scraper-runs" className="hover:text-gray-700">
          Scraper Runs
        </Link>
        <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-gray-900">{id.substring(0, 8)}...</span>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Scraper Run Detail</h1>
      </div>

      {/* Metadata Summary */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-sm font-medium text-gray-500">Tournament</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{run.tournament_name}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Type</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{run.scraper_type}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Status</div>
            <div className="mt-1">
              <span
                className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(
                  run.status
                )}`}
              >
                {run.status}
              </span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Duration</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : '-'}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Started</div>
            <div className="mt-1 text-sm text-gray-900">
              {new Date(run.started_at).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Completed</div>
            <div className="mt-1 text-sm text-gray-900">
              {run.completed_at ? new Date(run.completed_at).toLocaleString() : '-'}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Changes</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{run.changes_count || 0}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Run ID</div>
            <div className="mt-1 text-xs text-gray-600 font-mono">{run.id}</div>
          </div>
        </div>

        {run.error_message && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="text-sm font-medium text-red-800">Error</div>
            <div className="mt-1 text-sm text-red-700">{run.error_message}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      {run.s3_key && (
        <>
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('changes')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'changes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Changes ({detailedLog?.changes.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('log')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'log'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Full Log ({detailedLog?.logs.length || 0})
              </button>
            </nav>
          </div>

          {loadingLog ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : detailedLog ? (
            <>
              {/* Changes Tab */}
              {activeTab === 'changes' && (
                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  {detailedLog.changes.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">No changes recorded</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Entity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Field
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Old Value
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              New Value
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {detailedLog.changes.map((change, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                {new Date(change.timestamp).toLocaleTimeString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {change.entity}
                                {change.entityId && (
                                  <div className="text-xs text-gray-500">{change.entityId}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">
                                {change.field}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {formatValue(change.oldValue)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                                {formatValue(change.newValue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Full Log Tab */}
              {activeTab === 'log' && (
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  {detailedLog.logs.length === 0 ? (
                    <div className="text-center text-gray-500">No log entries</div>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto font-mono text-sm">
                      {detailedLog.logs.map((log, index) => (
                        <div key={index} className={`${getLevelColor(log.level)} flex gap-4`}>
                          <span className="text-gray-400">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                          <span className="font-semibold uppercase w-16">[{log.level}]</span>
                          <span className="text-gray-500">[{log.step}]</span>
                          <span className="flex-1">{log.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-500">
              Failed to load detailed log
            </div>
          )}
        </>
      )}

      {!run.s3_key && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-500">
          No detailed log available for this run
        </div>
      )}
    </div>
  );
}
